import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useCallback, useMemo } from 'react';

interface PageFiltersState {
  filters: Record<string, Record<string, any>>;
  setFilter: (pageKey: string, filterKey: string, value: any) => void;
  setFilters: (pageKey: string, partial: Record<string, any>) => void;
  clearFilters: (pageKey: string) => void;
  clearAllFilters: () => void;
}

export const usePageFiltersStore = create<PageFiltersState>()(
  persist(
    (set) => ({
      filters: {},
      setFilter: (pageKey, filterKey, value) =>
        set((state) => ({
          filters: {
            ...state.filters,
            [pageKey]: { ...state.filters[pageKey], [filterKey]: value },
          },
        })),
      setFilters: (pageKey, partial) =>
        set((state) => ({
          filters: {
            ...state.filters,
            [pageKey]: { ...state.filters[pageKey], ...partial },
          },
        })),
      clearFilters: (pageKey) =>
        set((state) => {
          const { [pageKey]: _, ...rest } = state.filters;
          return { filters: rest };
        }),
      clearAllFilters: () => set({ filters: {} }),
    }),
    {
      name: 'ccms_page_filters',
      storage: {
        getItem: (name) => {
          const value = localStorage.getItem(name);
          return value ? JSON.parse(value) : null;
        },
        setItem: (name, value) => {
          localStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          localStorage.removeItem(name);
        },
      },
      partialize: (state) =>
        ({ filters: state.filters }) as unknown as PageFiltersState,
    }
  )
);

/**
 * Hook for per-page filter persistence.
 * Generic and reusable across any list page in any service.
 *
 * @param pageKey - Unique key for the page (e.g. 'procurements/my-requests')
 * @param defaults - Default filter values when no persisted state exists
 * @returns [filters, setFilter, setFilters, clearFilters]
 *
 * @example
 * const [filters, setFilter, setFilters, clearFilters] = usePageFilters(
 *   'procurements/my-requests',
 *   { searchQuery: '', statusFilter: null, typeFilter: null }
 * );
 * // Read: filters.searchQuery
 * // Set one: setFilter('statusFilter', 2)
 * // Set many: setFilters({ statusFilter: null, typeFilter: null })
 * // Reset: clearFilters()
 */
export function usePageFilters<T extends Record<string, any>>(
  pageKey: string,
  defaults: T
): [T, (key: keyof T, value: any) => void, (partial: Partial<T>) => void, () => void] {
  const stored = usePageFiltersStore(
    useCallback((s: PageFiltersState) => s.filters[pageKey], [pageKey])
  );
  const filters = useMemo(
    () => ({ ...defaults, ...stored }) as T,
    [defaults, stored]
  );
  const setFilter = useCallback(
    (key: keyof T, value: any) =>
      usePageFiltersStore.getState().setFilter(pageKey, key as string, value),
    [pageKey]
  );
  const setFilters = useCallback(
    (partial: Partial<T>) =>
      usePageFiltersStore.getState().setFilters(pageKey, partial),
    [pageKey]
  );
  const clearFilters = useCallback(
    () => usePageFiltersStore.getState().clearFilters(pageKey),
    [pageKey]
  );
  return [filters, setFilter, setFilters, clearFilters];
}
